import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Container, Divider, Paper, Stack, Typography, alpha } from '@mui/material';
import { ArrowForward, CheckCircle, HourglassTop, MailOutline, ReceiptLong, Refresh } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface CheckoutItem {
  kind: 'setup' | 'monthly';
  title: string;
  amount_cents: number;
  paid: boolean;
  payment_url: string | null;
}

interface CustomerStatus {
  success: boolean;
  tier: string | null;
  retainerPaid: boolean;
  subscriptionStatus: string | null;
  topstepDashboardEnabled: boolean;
  topstepProvisionStatus: string | null;
  topstepCheckoutItems?: CheckoutItem[];
  canEnterPlatform?: boolean;
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

const PendingPaymentPage: React.FC = () => {
  const { user, logout } = useAuth();
  const [status, setStatus] = useState<CustomerStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/customers/status');
      setStatus(data);
    } catch {
      setError('Unable to refresh payment status. Your checkout links were also sent by email.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const checkoutItems = status?.topstepCheckoutItems || [];
  const dashboardReady = status?.canEnterPlatform === true;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#0B0B0D',
        display: 'flex',
        alignItems: 'center',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Paper
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 1,
            background: '#161616',
            border: '1px solid',
            borderColor: alpha('#fff', 0.1),
            textAlign: 'center',
          }}
        >
          <Stack spacing={3}>
            <Stack spacing={1.5} alignItems="center">
              {dashboardReady ? (
                <CheckCircle sx={{ fontSize: 64, color: '#4CAF50' }} />
              ) : (
                <HourglassTop sx={{ fontSize: 64, color: '#F44336' }} />
              )}
              <Typography variant="h4" fontWeight={900} color="white" gutterBottom>
                {dashboardReady ? 'Dashboard Ready' : 'Checkout Required'}
              </Typography>
              <Typography variant="body1" color="grey.400" sx={{ maxWidth: 620 }}>
                {dashboardReady
                  ? 'Your TopStep bot account is active. You can enter the dashboard now.'
                  : 'Complete the setup retainer and monthly subscription before the bot dashboard unlocks.'}
              </Typography>
            </Stack>

            {error && <Alert severity="warning" sx={{ textAlign: 'left' }}>{error}</Alert>}

            {loading ? (
              <Stack alignItems="center" spacing={1.5} sx={{ py: 3 }}>
                <CircularProgress size={28} />
                <Typography color="grey.500">Checking payment status...</Typography>
              </Stack>
            ) : dashboardReady ? (
              <Button component={RouterLink} to="/topstep/bot" variant="contained" endIcon={<ArrowForward />} sx={{ alignSelf: 'center', bgcolor: '#F44336', '&:hover': { bgcolor: '#D32F2F' } }}>
                Enter Dashboard
              </Button>
            ) : (
              <Stack spacing={2}>
                {checkoutItems.length > 0 ? checkoutItems.map((item) => (
                  <Box key={item.kind} sx={{ p: 2.5, borderRadius: 1, bgcolor: alpha('#fff', 0.04), border: '1px solid rgba(255,255,255,0.08)', textAlign: 'left' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                      <Stack spacing={0.75}>
                        <Chip
                          icon={item.paid ? <CheckCircle sx={{ fontSize: 16 }} /> : <ReceiptLong sx={{ fontSize: 16 }} />}
                          label={item.kind === 'setup' ? 'Upfront Retainer' : 'Monthly Subscription'}
                          size="small"
                          variant="outlined"
                          sx={{ alignSelf: 'flex-start', color: item.paid ? '#4CAF50' : '#FF8A80', borderColor: item.paid ? alpha('#4CAF50', 0.5) : alpha('#F44336', 0.45) }}
                        />
                        <Typography fontWeight={800} color="white">{item.title}</Typography>
                        <Typography variant="h5" fontWeight={900} color="white">{formatCents(item.amount_cents)}</Typography>
                      </Stack>
                      <Button
                        href={!item.paid && item.payment_url ? item.payment_url : undefined}
                        disabled={item.paid || !item.payment_url}
                        variant={item.paid ? 'outlined' : 'contained'}
                        endIcon={item.paid ? <CheckCircle /> : <ArrowForward />}
                        sx={item.paid ? { color: '#4CAF50', borderColor: alpha('#4CAF50', 0.45) } : { bgcolor: '#F44336', '&:hover': { bgcolor: '#D32F2F' } }}
                      >
                        {item.paid ? 'Paid' : 'Checkout'}
                      </Button>
                    </Stack>
                  </Box>
                )) : (
                  <Alert severity="info" icon={<MailOutline />} sx={{ textAlign: 'left' }}>
                    Payment request links were sent to <strong>{user?.email}</strong>. If they are missing, contact support@redexct.com.
                  </Alert>
                )}

                <Alert severity="info" sx={{ textAlign: 'left' }}>
                  Access stays locked until both payments are confirmed by the payment portal. The bot cannot be started from this account before activation.
                </Alert>
              </Stack>
            )}

            <Divider sx={{ borderColor: alpha('#fff', 0.08) }} />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
              <Button variant="outlined" startIcon={<Refresh />} onClick={loadStatus} disabled={loading} sx={{ color: 'grey.300', borderColor: alpha('#fff', 0.2) }}>
                Refresh Status
              </Button>
              <Button variant="text" color="inherit" onClick={logout} sx={{ color: 'grey.500' }}>
                Sign Out
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default PendingPaymentPage;
